// Transition Plan v1.0 lever L9 slice 1 — small bridge between the React app
// and the Tauri shell.
//
// The dashboard runs in TWO modes interchangeably:
//   1. Browser (Vite dev or FastAPI-served bundle) — the historical mode.
//   2. Tauri WebView (cargo tauri dev / built .msi) — adds native window
//      controls (always-on-top, future: system tray).
//
// Code under src/ must work in both. Helpers below detect the Tauri runtime
// via the `__TAURI_INTERNALS__` global the Tauri 2 bootstrap injects, then
// dynamically import @tauri-apps/api ONLY when present. Dynamic import is
// load-bearing: the package is a hard devDependency, but in browser mode
// we don't want a static import making the helper module fail to evaluate
// when `window.__TAURI_INTERNALS__` is undefined.

/** True when this bundle is running inside the Tauri WebView. */
export function isTauri(): boolean {
  if (typeof window === "undefined") return false;
  return (window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__ !== undefined;
}

/** Invoke a Tauri command. Returns null silently in browser mode so callers
 * can no-op without branching on `isTauri()` at every call site. */
export async function tauriInvoke<T>(
  cmd: string,
  args?: Record<string, unknown>,
): Promise<T | null> {
  if (!isTauri()) return null;
  try {
    const mod = await import("@tauri-apps/api/core");
    return (await mod.invoke<T>(cmd, args)) ?? null;
  } catch (err) {
    // Don't let a Tauri-side failure crash the panel — the rest of the
    // dashboard still works fine in browser mode.
    console.warn(`tauri invoke(${cmd}) failed:`, err);
    return null;
  }
}

/** Toggle the always-on-top state of the main window. Returns the new state,
 * or null if we're not in Tauri. */
export async function toggleAlwaysOnTop(): Promise<boolean | null> {
  return tauriInvoke<boolean>("toggle_always_on_top");
}

/** Read the current always-on-top state. Lets the toggle render correctly
 * on first paint. */
export async function isAlwaysOnTop(): Promise<boolean | null> {
  return tauriInvoke<boolean>("is_always_on_top");
}

/** Fire a native OS notification via the Tauri shell. No-op in browser
 * mode (returns null), so callers can fire unconditionally. */
export async function nativeNotify(title: string, body: string): Promise<void> {
  await tauriInvoke<null>("notify", { title, body });
}
